import React, { useState, useMemo } from "react";
import Table, { Row, Data } from "../../components/ui/table/table";

import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import {
  getFormattedDate,
  getAmountWithCurrency,
  formatAmount,
  getCurrentFormattedDate,
} from "../../utils/utils";
import PageWrapper from "../../layouts/PageWrapper";
import Section from "../../layouts/Section";
import { Input, Select } from "../../components/form/formControls";
import { StatusBadge } from "../../components/StatusBadge";

import { useDebounce } from "../../hooks/useDebounce";

import { fetchReportB2B } from "../../api/wallet-service";
import { downloadReportB2B } from "../../utils/downloadReport";

import ViewDocumentDrawer from "./ViewDocumentDrawer";
import SubUserSelector from "./SubUserSelector";

import { FileText } from "react-feather";
import { Button } from "../../components/ui/buttons/Button";
import Pagination from "../../components/Pagination";
import { usePagination } from "../../hooks/usePagination";

import { useLocale } from "../../hooks/useLocale";

const ReportsCardLoad = () => {
  // globals
  const inputDate = useSelector((s) => s.global.inputDate);
  const currentUser = useSelector((s) => s.auth.currentUser);
  const { page } = usePagination();

  // user selected from the sub user list
  const selectedUser = useSelector((s) => s.reports.selectedUser);

  // Current Selcted Item
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isDownoadingReport, setIsDownoadingReport] = useState(false);
  const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);

  // Handling Filters
  const filterSchema = {
    status: "All",
    requesterName: "",
    recipientName: "",
    orderId: "",
    referenceNumber: "",
  };
  const [filters, setFilters] = useState(filterSchema);
  const handleFilter = (filteredValues) => setFilters(filteredValues);
  const debouncedHandleFilters = useDebounce(handleFilter);

  const { localize } = useLocale(["reports"]);

  const reportQuery = useQuery(
    ["reports/payments", filters, inputDate, page, selectedUser?.userHashId],
    () =>
      fetchReportB2B({
        clientHashId: selectedUser?.userHashId,
        userHashId: selectedUser?.userHashId,
        dateRange: inputDate.date,
        filters,
        page,
      }),
    {
      enabled: !!selectedUser,
    }
  );

  // download report
  const handleDownloadReport = () => {
    setIsDownoadingReport(true);
    downloadReportB2B({
      clientHashId: currentUser.clientHashId,
      userHashId: selectedUser.userHashId,
      dateRange: inputDate.date,
      email: selectedUser.emailId,
    })
      .then((res) => {
        setIsDownoadingReport(false);
      })
      .catch((err) => {});
  };

  return (
    <>
      <PageWrapper>
        {/* Page Header */}
        <Section>
          <div className="d-flex">
            <h3>{localize("heading")}</h3>
            <div className="ms-auto">
              <SubUserSelector />
            </div>
          </div>
        </Section>

        {/* Table Data */}
        <Section>
          <>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <h5 className="text-muted text-uppercase">
                {localize("b2b.heading")}
              </h5>

              <Button
                text={localize("b2b.actions.downloadReport")}
                loading={isDownoadingReport}
                className="btn btn-primary"
                onClick={handleDownloadReport}
              />
            </div>

            {/* Table Data */}
            <Table
              loading={reportQuery.isLoading}
              cols={localize("b2b.table.cols", { returnObjects: true })}
              dataSize={reportQuery?.data?.content.length}
              filters={
                <TableFilters
                  filterSchema={filters}
                  handleFilter={debouncedHandleFilters}
                />
              }
            >
              {/* =============== Display Data from API =============*/}
              {reportQuery?.data?.content.map((item) => (
                <>
                  <Row>
                    {/* Status */}
                    <Data>
                      <StatusBadge status={item.status} />
                    </Data>
                    <Data>
                      {item.requesterName
                        ? `${item.requesterName} (${item.requesterClientName})`
                        : "-"}
                    </Data>
                    <Data>
                      {item.recipientName
                        ? `${item.recipientName} (${item.taggedClientName})`
                        : "-"}
                    </Data>
                    <Data>{item.orderId ? item.orderId : "-"}</Data>
                    <Data>
                      {item.requesterClientHashId
                        ? item.requesterClientHashId
                        : "-"}
                    </Data>
                    <Data>
                      {item.amount
                        ? getAmountWithCurrency(
                            formatAmount(item.amount),
                            item.currency
                          )
                        : "-"}
                    </Data>
                    <Data>
                      {item.referenceNumber ? item.referenceNumber : "-"}
                    </Data>
                    <Data>{getFormattedDate(item.createdAt)}</Data>
                    <Data>{item.approverName ? item.approverName : "-"}</Data>
                    <Data>
                      {item.paymentReceiptFileName ? (
                        <a
                          className="btn btn-light"
                          onClick={() => {
                            setCurrentRecord(item);
                            setIsDocumentDrawerOpen(true);
                          }}
                        >
                          <FileText size={12} />
                          <span className="ms-1 text-capitalize">
                            {localize("b2b.actions.viewDocuments")}
                          </span>
                        </a>
                      ) : (
                        <a className="btn btn-light disabled" disabled>
                          <FileText size={12} />
                          <span className="ms-1 text-capitalize">
                            {localize("b2b.actions.viewDocuments")}
                          </span>
                        </a>
                      )}
                    </Data>
                    <Data>{item.remarks ? item.remarks : "-"}</Data>
                  </Row>
                </>
              ))}
              {/* show empty list */}
            </Table>

            {/* ======= Pagination =======*/}
            <div className="d-flex justify-content-end mb-5">
              <Pagination
                show={reportQuery.data?.content.length}
                totalPages={reportQuery.data?.totalPages}
                handlePageSelect={(page) =>
                  setFilters({ ...filters, page: page })
                }
              />
            </div>
          </>
        </Section>

        {/* View Documents Drawer */}
        {isDocumentDrawerOpen && (
          <ViewDocumentDrawer
            title={localize("b2b.actions.viewDocuments")}
            cardItem={currentRecord}
            onClose={() => {
              setIsDocumentDrawerOpen(false);
            }}
          />
        )}
      </PageWrapper>
    </>
  );
};

const TableFilters = ({ filterSchema, handleFilter }) => {
  {
    /**
     *
     * Note : "name" attribute is important for filter controls
     * Interpreted as : [name] and filterSchema.name
     *
     */
  }
  const statusOptions = [
    {
      value: "All",
      text: "All",
    },
    {
      value: "Approved",
      text: "Approved",
    },
    {
      value: "Unsettled",
      text: "Unsettled",
    },
    {
      value: "Requested",
      text: "Requested",
    },
    {
      value: "Rejected",
      text: "Rejected",
    },
  ];

  // table filter has its own state for persisting the filter values
  const [filteredValues, setFilteredValues] = useState(filterSchema);

  const handleFilteredValues = (event) => {
    const { name, value } = event.target;
    setFilteredValues({ ...filteredValues, [name]: value });
    handleFilter({ ...filteredValues, [name]: value, page: 0 });
  };

  return (
    <>
      <Row>
        <Data>
          <Select
            name="status"
            value={filteredValues.status}
            onChange={(event) => handleFilteredValues(event)}
          >
            {statusOptions.map((option) => (
              <option value={option.value}>{option.text}</option>
            ))}
          </Select>
        </Data>
        <Data>
          <Input
            name="requesterName"
            value={filteredValues.requesterName}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data>
          <Input
            name="recipientName"
            value={filteredValues.recipientName}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data>
          <Input
            name="orderId"
            value={filteredValues.orderId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data />
        <Data />
        <Data>
          <Input
            name="referenceNumber"
            value={filteredValues.referenceNumber}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data />
        <Data />
        <Data />
        <Data />
      </Row>
    </>
  );
};

export default ReportsCardLoad;
